/**
 * DataLayer utility for Google Tag Manager integration
 * Provides safe, error-handled access to window.dataLayer
 */

// Initialize dataLayer if it doesn't exist
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Check if we're in development mode
 */
const isDev = () => {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
};

/**
 * Log tracking errors in development, silent in production
 */
const logError = (context, error) => {
  if (isDev()) {
    console.error(`[Tracking Error - ${context}]:`, error);
  }
};

/**
 * Log tracking events in development
 */
const logEvent = (eventName, data) => {
  if (isDev()) {
    console.log(`[DataLayer Push - ${eventName}]:`, data);
  }
};

/**
 * Push an event to the dataLayer with automatic timestamp
 * @param {string} eventName - The event name (snake_case)
 * @param {Object} eventData - Additional event data
 * @returns {boolean} - Whether the push was successful
 */
export const pushEvent = (eventName, eventData = {}) => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    const payload = {
      event: eventName,
      timestamp: Date.now(),
      ...eventData,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    
    logEvent(eventName, payload);
    return true;
  } catch (error) {
    logError('pushEvent', error);
    return false;
  }
};

/**
 * Push multiple events at once
 * @param {Array} events - Array of {eventName, eventData} objects
 */
export const pushEvents = (events) => {
  try {
    events.forEach(({ eventName, eventData }) => {
      pushEvent(eventName, eventData);
    });
  } catch (error) {
    logError('pushEvents', error);
  }
};

/**
 * Get the current dataLayer contents (useful for debugging)
 * @returns {Array} - Copy of current dataLayer
 */
export const getDataLayer = () => {
  try {
    if (typeof window === 'undefined') {
      return [];
    }
    return [...(window.dataLayer || [])];
  } catch (error) {
    logError('getDataLayer', error);
    return [];
  }
};

/**
 * Send an event directly via gtag() for GA4
 * This bypasses GTM triggers and sends directly to any GA4 property
 * that was configured via gtag or loaded by GTM
 * @param {string} eventName - The event name (snake_case, use GA4 recommended names)
 * @param {Object} eventData - Event parameters
 * @returns {boolean} - Whether the call was successful
 */
export const sendGA4Event = (eventName, eventData = {}) => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    // Use gtag() if available (defined in index.html before GTM)
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventData);
      logEvent(`gtag:${eventName}`, eventData);
      return true;
    }

    return false;
  } catch (error) {
    logError('sendGA4Event', error);
    return false;
  }
};

export default pushEvent;
